export const UNASSIGNED_FILTER_OPTION_ID = '__unassigned__'

/** One word for "we don't have this value", used by a cell and by its filter option alike. */
export const UNKNOWN_VALUE_LABEL = 'Unknown'

type FilterOption = {
    id: string
    label: string
    order?: number
}

export type FilterDefinition<TRow> = {
    id: string
    label: string
    columnOnly?: boolean
    /** The column this filter governs, when it differs from the filter id. */
    columnId?: string
    /** Starts with every option off, so its column is opt-in rather than shown by default. */
    defaultHidden?: boolean
    getOptions: (rows: TRow[]) => FilterOption[]
    matches: (row: TRow, selectedOptionIds: string[]) => boolean
}

type FilterEntry<TRow> = {
    definition: FilterDefinition<TRow>
    options: FilterOption[]
    selectedOptionIds: string[]
    mode: FilterMode
}

/** What a filter is doing: no rows fall into it, the user switched it off, it narrows, or it lets everything through. */
type FilterMode = 'noOptions' | 'off' | 'narrowing' | 'all'

export const getFilterColumnId = <TRow>(definition: FilterDefinition<TRow>) => definition.columnId ?? definition.id

/**
 * Switched off means nothing is left selected, which hides the column instead of emptying the
 * table -- filtering to nothing was a dead end whose only way out was undoing the click.
 */
const getFilterMode = (options: FilterOption[], selectedOptionIds: string[]): FilterMode => {
    if (!options.length) return 'noOptions'
    if (!selectedOptionIds.length) return 'off'

    return selectedOptionIds.length === options.length ? 'all' : 'narrowing'
}

/**
 * The options the user unchecked, per scope, so several tables share one set of filters.
 *
 * Only works while every table in a scope offers the same options (see UserSettingsView.filters):
 * a stored id then means the same thing everywhere. Once option lists differ from table to table,
 * neither what was checked nor what was unchecked can describe an option the user never saw.
 */
export type UncheckedOptionIdsByFilterId = Record<string, string[]>

export const buildFilterEntry = <TRow>(
    definition: FilterDefinition<TRow>,
    rows: TRow[],
    uncheckedByFilterId: UncheckedOptionIdsByFilterId
): FilterEntry<TRow> => {
    const options = definition.getOptions(rows)
    const optionIds = options.map((option) => option.id)
    // An opt-in column starts with everything unchecked, which is what keeps it hidden.
    const unchecked = new Set(uncheckedByFilterId[definition.id] ?? (definition.defaultHidden ? optionIds : []))
    const selectedOptionIds = optionIds.filter((optionId) => !unchecked.has(optionId))

    return { definition, options, selectedOptionIds, mode: getFilterMode(options, selectedOptionIds) }
}

/** What to store once the user has changed which of this table's options are checked. */
export const toUncheckedOptionIds = (
    options: FilterOption[],
    selectedOptionIds: string[],
    storedUncheckedOptionIds: string[] = []
) => {
    const shownHere = new Set(options.map((option) => option.id))
    // Anything the menu could not show is left as it was, for the moment a vocabulary is still
    // loading. With the option lists stable there is normally nothing here.
    const uncheckedElsewhere = storedUncheckedOptionIds.filter((optionId) => !shownHere.has(optionId))

    return [
        ...uncheckedElsewhere,
        ...options.map((option) => option.id).filter((optionId) => !selectedOptionIds.includes(optionId)),
    ]
}

export const getUniqueFilterOptions = (options: FilterOption[]) => {
    const seenOptionIds = new Set<string>()

    return options
        .filter((option) => {
            if (seenOptionIds.has(option.id)) return false

            seenOptionIds.add(option.id)

            return true
        })
        .sort((left, right) => {
            const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
            const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER

            return leftOrder === rightOrder ? left.label.localeCompare(right.label) : leftOrder - rightOrder
        })
}

/** Keyed by the column that displays the value, so search can skip the hidden ones. */
export type SearchValuesByColumnId = Record<string, string | undefined | null>

export const applySearch = <TRow>(
    rows: TRow[],
    searchQuery: string,
    getSearchValues: (row: TRow) => SearchValuesByColumnId,
    hiddenColumnIds: string[] = []
) => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase()

    if (!normalizedQuery) {
        return rows
    }

    const hidden = new Set(hiddenColumnIds)

    return rows.filter((row) =>
        Object.entries(getSearchValues(row)).some(
            ([columnId, value]) =>
                !hidden.has(columnId) && !!value && value.toLocaleLowerCase().includes(normalizedQuery)
        )
    )
}

/** Only narrowing filters are asked: every row falls into one of the options, so "all" matches all. */
export const applyFilters = <TRow>(rows: TRow[], filterEntries: FilterEntry<TRow>[]) => {
    const narrowingEntries = filterEntries.filter((entry) => entry.mode === 'narrowing')

    if (!narrowingEntries.length) {
        return rows
    }

    return rows.filter((row) =>
        narrowingEntries.every(({ definition, selectedOptionIds }) => definition.matches(row, selectedOptionIds))
    )
}
